<template>
    <div>
        <Sidebar></Sidebar>

        <div class="content-wrapper content-wrapper-exemption">
            <div class="img-desktop img-desktop-manual-control d-lg-inline-block"></div>

            <Header></Header>

            <section class="content">
                <div class="container-fluid">
                    <div class="row">
                        <div class="col-md-10 form-request-content-large">
                            <div class="request-title">
                                <h1>Paramétrage manuel</h1>
                            </div>

                            <div class="request-title">
                                <h2>Gestion des cas de dispenses facultatifs</h2>
                                <ButtonSpinner :is-loading="isLoading" />
                            </div>

                            <div v-if="reasons.length === 0 && isLoading === false" class="alert alert-warning">
                                Aucun cas de dispense facultatif n'est disponible actuellement.
                            </div>

                            <div class="form-group" v-else>
                                <form
                                    name="exemption"
                                    method="post"
                                    enctype="multipart/form-data"
                                    novalidate="novalidate"
                                    class="needs-validation mb-3"
                                >
                                    <div v-for="(reason, index) in reasons"
                                         :key="index"
                                         class="form-group clearfix"
                                    >
                                        <div class="icheck-primary d-inline optional-motif-input-group">
                                            <input type="checkbox"
                                                   :id="reason.id"
                                                   :value="reason.id"
                                                   v-model="formData.optionalReasons"
                                            >
                                            <label :for="reason.id">
                                                <span>{{ reason.titleHro }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <hr />

                            <div class="request-title">
                                <h2>Renouvellement automatique des demandes de dispenses</h2>
                            </div>

                            <div class="form-group">
                                <form
                                    name="renewal"
                                    method="post"
                                    enctype="multipart/form-data"
                                    novalidate="novalidate"
                                    class="needs-validation mb-3"
                                >
                                    <div class="form-group">
                                        <label for="startDate">Traitement actif à partir du</label>
                                        <div class="input-group">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text">
                                                    <i class="far fa-calendar-alt" />
                                                </span>
                                            </div>
                                            <masked-input
                                                type="text"
                                                id="renewalActiveFrom"
                                                mask="11/11/1111"
                                                class="form-control float-right"
                                                :class="{ 'is-invalid': $v.formData.renewalActiveFrom.$error }"
                                                @input="delayTouch($v.formData.renewalActiveFrom)"
                                                v-model="formData.renewalActiveFrom"
                                            />
                                        </div>
                                        <FormError error="Format de date incorrect" v-if="$v.formData.renewalActiveFrom.$error" />
                                    </div>

                                    <div class="form-group">
                                        <label class="required-field-asterisks" for="renewalPeriodicity">Périodicité</label>
                                        <select id="renewalPeriodicity" class="form-control select2" v-model="formData.renewalPeriodicity">
                                            <option v-for="(periodicityTitle, periodicityValue) in periodicities" :key="periodicityValue" :value="periodicityValue">
                                                {{ periodicityTitle }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label class="required-field-asterisks" for="renewalDelayInDays">Délai avant la fin de la dispense pour le déclenchement du renouvellement</label>
                                        <select id="renewalDelayInDays" class="form-control select2" v-model="renewalDelayInDays">
                                            <option v-for="(delayTitle, delayValue) in renewalDelays" :key="delayValue" :value="delayValue">
                                                {{ delayTitle }}
                                            </option>
                                        </select>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div class="input-group col-md-10 mx-sm-auto my-md-4 mt-4">
                            <div class="mx-sm-auto button-box">
                                <button
                                    class="btn btn-success btn-lg ml-4 btn-link btn__save"
                                    :disabled="isLoading"
                                    @click.prevent="submit"
                                >
                                    <span>Enregistrer les paramètres</span>
                                    <ButtonSpinner :isLoading="isSaving"></ButtonSpinner>
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>

        <Footer></Footer>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import MaskedInput from 'vue-masked-input';
    import moment from 'moment';

    import Sidebar from '../components/Sidebar';
    import Footer from '../components/Footer';
    import ButtonSpinner from '../components/ButtonSpinner';
    import Header from '../components/Header';
    import { fetchReasons } from '@/service/api/exemptions.api';
    import { saveEnterpriseOptionalReasons } from '@/service/api/dues.api';
    import { messageTexts, successMessage } from '@/utils/sweet-alert-options';
    import { fetchEnterpriseSettingsByEnterpriseUuid } from '@/service/api/enterprise.api';
    import FormError from '@/components/FormError.vue';
    import { isValidDateYearConstraint, validDateConstraint } from '@/utils/validation';
    import ValidationDelayTouch from '@/mixins/validationDelayTouch';

    export default {
        mixins: [ValidationDelayTouch],
        data() {
            return {
                formData: {
                    optionalReasons: [],
                    renewalActiveFrom: '',
                    renewalPeriodicity: '',
                    renewalDelayInDays: null,
                },
                isLoading: null,
                isSaving: false,
                reasons: [],
                enterpriseSettings: null,
                periodicities: {
                    never: 'Aucun traitement automatique',
                    daily: 'Traitement quotidien',
                    weekly: 'Traitement hebdomadaire',
                    bimonthly: 'Traitement bimensuel',
                    monthly: 'Traitement mensuel',
                },
                renewalDelays: {
                    7: '7 jours',
                    14: '14 jours',
                    30: '30 jours',
                    45: '45 jours',
                    60: '60 jours',
                    90: '90 jours',
                },
            };
        },
        async created() {
            await this.fetchReasons();
            await this.fetchEnterpriseSettings();
            await this.processEnterpriseSettingsReasons();
            await this.processEnterpriseSettings();
        },
        computed: {
            ...mapGetters({
                enterpriseUuid: 'enterpriseUuid',
            }),
            renewalDelayInDays: {
                get() {
                    return this.formData.renewalDelayInDays?.toString()
                },
                set(val) {
                    this.formData.renewalDelayInDays = Number(val)
                }
            }
        },
        methods: {
            ...mapActions({
                setEnterpriseSettingsId: 'setEnterpriseSettingsId',
            }),
            async submit() {
                try {
                    this.isSaving = true;
                    const payload = {
                        ...this.formData,
                        ...(this.formData.renewalActiveFrom && {
                            renewalActiveFrom: moment(this.formData.renewalActiveFrom, 'DD/MM/YYYY').format('YYYY-MM-DD')
                        }),
                    };
                    await saveEnterpriseOptionalReasons(payload);

                    this.isSaving = false;

                    const messages = [messageTexts.settingsChangeSuccess];
                    if (this.reasons.length > 0) {
                        messages.push(messageTexts.manualMotifsChangeSuccess);
                    }

                    this.$swal({ width: '400px', text: messages.join(' - '), ...successMessage });
                    await this.$router.push({ name: 'exemption-list' });
                } catch (error) {
                    this.isSaving = false;
                }
            },
            async fetchReasons() {
                const startLoader = setTimeout(() => {
                    this.isLoading = true;
                }, 1000);

                let { data } = await fetchReasons({
                    params: {
                        isOptional: true,
                        enterpriseUuid: this.enterpriseUuid,
                    },
                });
                this.reasons = data;
                startLoader && clearTimeout(startLoader);
                this.isLoading = false;
            },
            async fetchEnterpriseSettings() {
                  const { data } = await fetchEnterpriseSettingsByEnterpriseUuid(this.enterpriseUuid);
                  this.enterpriseSettings = data;
                  if (this.enterpriseSettings.renewalActiveFrom) {
                      const renewalActiveFrom = moment(this.enterpriseSettings.renewalActiveFrom);
                      renewalActiveFrom.isValid() && (this.enterpriseSettings.renewalActiveFrom = renewalActiveFrom.format('DD/MM/YYYY'));
                  }
            },
            async processEnterpriseSettingsReasons() {
                if (this.enterpriseSettings) {
                    for (const reason of this.enterpriseSettings.optionalReasons) {
                        this.formData.optionalReasons.push(reason.id)
                    }
                }
            },
            async processEnterpriseSettings() {
                if (this.enterpriseSettings) {
                    this.formData.renewalActiveFrom = this.enterpriseSettings.renewalActiveFrom;
                    this.formData.renewalPeriodicity = this.enterpriseSettings.renewalPeriodicity;
                    this.formData.renewalDelayInDays = this.enterpriseSettings.renewalDelayInDays;
                }
            },
        },
        validations() {
            return {
                formData: {
                    renewalActiveFrom: {
                        validDate: validDateConstraint,
                        isValidDateYear: isValidDateYearConstraint,
                    },
                },
            };
        },
        components: {
            FormError,
            Header,
            Sidebar,
            Footer,
            ButtonSpinner,
            MaskedInput,
        },
    }
</script>